import React from 'react';
import type { TextBlock } from '@/features/chat/utils/assistant-content';

// Render rich text blocks
export function RichText({ blocks }: { blocks: TextBlock[] }) {
  if (blocks.length === 0) {
    return null
  }

  const rendered: React.ReactNode[] = []

  blocks.forEach((block, index) => {
    if (block.type === 'heading' && block.value) {
      const HeadingTag = `h${block.level || 2}` as keyof React.JSX.IntrinsicElements
      rendered.push(
        <HeadingTag key={`h-${index}`} className={`heading-${block.level || 2}`}>
          {renderInline(block.value)}
        </HeadingTag>,
      )
    } else if (block.type === 'paragraph' && block.value) {
      rendered.push(
        <p key={`p-${index}`} className="whitespace-pre-wrap">
          {renderInline(block.value)}
        </p>,
      )
    } else if (block.type === 'list' && block.items) {
      rendered.push(
        <ul key={`list-${index}`} className="ml-4 list-disc space-y-1">
          {block.items.map((item, itemIndex) => (
            <li key={`li-${index}-${itemIndex}`}>{renderInline(item)}</li>
          ))}
        </ul>,
      )
    } else if (block.type === 'spacer') {
      rendered.push(
        <div
          key={`spacer-${index}`}
          className="h-2"
          aria-hidden
        />,
      )
    }
  })

  return <div className="flex flex-col gap-3">{rendered}</div>
}

// Render inline markdown (bold, italic, links)
function renderInline(text: string): React.ReactNode[] {
  const tokens = text.split(/(\*\*.+?\*\*|\*.+?\*|\[.+?\]\(.+?\))/g)

  return tokens.map((token, index) => {
    // Bold: **text**
    if (token.startsWith('**') && token.endsWith('**')) {
      return (
        <strong key={`bold-${index}`}>
          {token.slice(2, -2)}
        </strong>
      )
    }

    // Italic: *text* (but not **)
    if (token.startsWith('*') && token.endsWith('*') && !token.startsWith('**')) {
      return (
        <em key={`italic-${index}`}>
          {token.slice(1, -1)}
        </em>
      )
    }

    // Link: [text](url)
    if (token.startsWith('[') && token.includes('](') && token.endsWith(')')) {
      const match = token.match(/^\[(.+?)\]\((.+?)\)$/)
      if (match) {
        const [, label, href] = match
        const isExternal = /^https?:/i.test(href)
        return (
          <a
            key={`link-${index}`}
            href={href}
            target={isExternal ? '_blank' : undefined}
            rel={isExternal ? 'noopener noreferrer' : undefined}
            className="font-medium text-blue-600 underline-offset-2 hover:underline"
          >
            {label}
          </a>
        )
      }
    }

    return <span key={`text-${index}`}>{token}</span>
  })
}
